/*
* === Navbar.js (Updated - Dynamic Tenant Feature Toggles Applied) ===
*/
import React, { useState, useContext, useMemo, useRef, useEffect } from 'react';
import { tenantConfig } from '../config/tenantConfig';
import { 
    AppBar, Toolbar, Typography, Button, Box, Avatar, Menu, MenuItem, 
    IconButton, Drawer, List, ListItem, ListItemButton, ListItemIcon, 
    ListItemText, Divider, useTheme, useMediaQuery, Badge, Tooltip 
} from '@mui/material';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { ColorModeContext } from '../context/ThemeContext';
import { useSettings } from '../context/SettingsContext';

// Icons
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';
import MenuIcon from '@mui/icons-material/Menu';
import DashboardIcon from '@mui/icons-material/Dashboard';
import SearchIcon from '@mui/icons-material/Search';
import AssessmentIcon from '@mui/icons-material/Assessment';
import PeopleIcon from '@mui/icons-material/People';
import LogoutIcon from '@mui/icons-material/Logout';
import AppRegistrationIcon from '@mui/icons-material/AppRegistration';
import LoginIcon from '@mui/icons-material/Login';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';
import SecurityIcon from '@mui/icons-material/Security';
import MailIcon from '@mui/icons-material/Mail';
import ArticleIcon from '@mui/icons-material/Article';
import SchoolIcon from '@mui/icons-material/School';
import PersonIcon from '@mui/icons-material/Person';
import SettingsIcon from '@mui/icons-material/Settings';
import GavelIcon from '@mui/icons-material/Gavel'; 
import CardMembershipIcon from '@mui/icons-material/CardMembership'; 
import RateReviewIcon from '@mui/icons-material/RateReview'; 
import ReviewModal from './ReviewModal'; 
import StarIcon from '@mui/icons-material/Star';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import OutboxIcon from '@mui/icons-material/Outbox';

const Navbar = () => {
    const { user, logout, unreadCount } = useAuth();
    const { logoUrl, platformName } = useSettings();
    const theme = useTheme();
    const colorMode = useContext(ColorModeContext);
    const navigate = useNavigate();
    const location = useLocation();
    
    // FIX 1: Changed 'md' to 'lg' so the hamburger kicks in earlier for SuperUsers
    const isMobile = useMediaQuery(theme.breakpoints.down('lg')); 
    
    const [anchorEl, setAnchorEl] = useState(null);
    const [mobileOpen, setMobileOpen] = useState(false);
    const [reviewOpen, setReviewOpen] = useState(false);
    
    // --- RBAC & TENANT Menu Configuration ---
    const allMenuItems = [
        { text: 'Dashboard', icon: <DashboardIcon />, path: '/applicant/dashboard', roles: ['Applicant'] },
        { text: 'My Documents', icon: <ArticleIcon />, path: '/applicant/documents', roles: ['Applicant'] },
        { text: 'My Progress', icon: <SchoolIcon />, path: '/applicant/progress', roles: ['Applicant'] },
        { text: 'Dashboard', icon: <DashboardIcon />, path: '/admin/dashboard', roles: ['Admin', 'SuperUser'] },
        { text: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard/reviewer', roles: ['Reviewer'] },
        { text: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard/approver', roles: ['Approver'] },
        { text: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard/finance', roles: ['Finance'] },
        { text: 'Applications', icon: <SearchIcon />, path: '/admin/search', roles: ['Admin', 'SuperUser', 'Reviewer', 'Approver', 'Finance'] },
        { text: 'Bursaries', icon: <BusinessCenterIcon />, path: '/admin/bursaries', roles: ['Admin', 'SuperUser', 'Approver'] },
        { text: 'Users', icon: <PeopleIcon />, path: '/admin/users', roles: ['Admin', 'SuperUser'] },
        { text: 'Email Queue', icon: <OutboxIcon />, path: '/admin/queue', roles: ['Admin', 'SuperUser'] },

        // 🚨 FEATURE TOGGLES: Dynamic Admin Menus based on tenantConfig
        ...(tenantConfig.features.showContracts ? [
            { text: 'Contracts', icon: <GavelIcon />, path: '/admin/contracts', roles: ['Admin', 'SuperUser'] }
        ] : []),

        ...(tenantConfig.features.showBillingDashboard ? [
            { text: 'Billing & Plan', icon: <CreditCardIcon />, path: '/funder/billing', roles: ['Finance', 'Admin', 'SuperUser'] },
            { text: 'Subscriptions', icon: <CardMembershipIcon />, path: '/admin/subscriptions', roles: ['SuperUser'] }
        ] : []),

        { text: 'Reviews', icon: <RateReviewIcon />, path: '/admin/reviews', roles: ['SuperUser'] },
        
        ...(tenantConfig.features.showReports ? [
            { text: 'Reports', icon: <AssessmentIcon />, path: '/admin/reports', roles: ['Admin', 'SuperUser', 'Finance', 'Approver'] }
        ] : []),

        ...(tenantConfig.features.showTemplates ? [
            { text: 'Templates', icon: <MailIcon />, path: '/admin/templates', roles: ['Admin', 'SuperUser'] }
        ] : [])
    ];

    const menuItems = useMemo(() => {
        if (!user) return [];
        return allMenuItems.filter(item => item.roles.includes(user.role));
    }, [user]);

    // --- Scrollable Menu Logic ---
    const scrollContainerRef = useRef(null);
    const [canScrollLeft, setCanScrollLeft] = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(false);

    const checkScroll = () => {
        if (scrollContainerRef.current) {
            const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
            setCanScrollLeft(scrollLeft > 0);
            // Math.ceil handles fractional pixel scaling on certain high-res displays
            setCanScrollRight(Math.ceil(scrollLeft + clientWidth) < scrollWidth);
        }
    };

    // Check scroll state on mount, when menu items change, and on window resize
    useEffect(() => {
        checkScroll();
        window.addEventListener('resize', checkScroll);
        return () => window.removeEventListener('resize', checkScroll);
    }, [menuItems]);

    const handleScroll = (direction) => {
        if (scrollContainerRef.current) {
            const scrollAmount = 250; // Distance to scroll per click
            scrollContainerRef.current.scrollBy({ 
                left: direction === 'left' ? -scrollAmount : scrollAmount, 
                behavior: 'smooth' 
            });
        }
    };

    const handleMenu = (event) => setAnchorEl(event.currentTarget);
    const handleClose = () => setAnchorEl(null);
    
    const handleLogout = () => {
        handleClose();
        logout();
        navigate('/login');
    };

    const handleDrawerToggle = () => setMobileOpen(!mobileOpen);

    const drawer = (
        <Box onClick={handleDrawerToggle} sx={{ textAlign: 'center' }}>
            <Typography variant="h6" sx={{ my: 2, fontWeight: 'bold', color: 'primary.main' }}>&nbsp;</Typography>
            <Divider />
                {/* ALWAYS VISIBLE IN MOBILE DRAWER: Pricing */}
                <List>
                {/* 🚨 FEATURE TOGGLE: Hide Pricing from the mobile menu for JB Marks */}
                {tenantConfig.features.showBillingDashboard && (
                    <>
                        <ListItemButton component={Link} to="/pricing">
                            <ListItemIcon><LocalOfferIcon color="primary" /></ListItemIcon>
                            <ListItemText primary="Pricing Plans" primaryTypographyProps={{ fontWeight: 'bold' }} />
                        </ListItemButton>
                        <Divider />
                    </>
                )}

                {menuItems.map((item) => (
                    <ListItem key={item.text + item.path} disablePadding>
                        <ListItemButton component={Link} to={item.path} selected={location.pathname === item.path}>
                            <ListItemIcon sx={{ color: location.pathname === item.path ? 'primary.main' : 'inherit' }}>{item.icon}</ListItemIcon>
                            <ListItemText primary={item.text} />
                        </ListItemButton>
                    </ListItem>
                ))}
                
                {user ? (
                    <>
                        <Divider sx={{ my: 1 }} />
                        <ListItemButton component={Link} to="/messages">
                            <ListItemIcon><Badge badgeContent={unreadCount} color="error"><MailIcon /></Badge></ListItemIcon>
                            <ListItemText primary="Messages" />
                        </ListItemButton>
                         {user.role === 'SuperUser' && (
                            <>
                                <ListItemButton component={Link} to="/admin/security-hub">
                                    <ListItemIcon><SecurityIcon /></ListItemIcon>
                                    <ListItemText primary="Security Hub" />
                                </ListItemButton>
                                <ListItemButton component={Link} to="/admin/settings">
                                    <ListItemIcon><SettingsIcon /></ListItemIcon>
                                    <ListItemText primary="Settings" />
                                </ListItemButton>
                            </>
                        )}
                        <ListItemButton component={Link} to="/profile">
                             <ListItemIcon><PersonIcon /></ListItemIcon>
                             <ListItemText primary="Profile" />
                        </ListItemButton>
                        <ListItemButton onClick={handleLogout}>
                            <ListItemIcon><LogoutIcon /></ListItemIcon>
                            <ListItemText primary="Logout" />
                        </ListItemButton>
                    </>
                ) : (
                    <>
                         <ListItemButton component={Link} to="/login">
                            <ListItemIcon><LoginIcon /></ListItemIcon>
                            <ListItemText primary="Login" />
                        </ListItemButton>
                        <ListItemButton component={Link} to="/register">
                            <ListItemIcon><AppRegistrationIcon /></ListItemIcon>
                            <ListItemText primary="Register" />
                        </ListItemButton>
                    </>
                )}
            </List>
        </Box>
    );

    const getInitials = () => {
        if (user?.FirstNames) return user.FirstNames.charAt(0).toUpperCase();
        if (user?.fullName) return user.fullName.charAt(0).toUpperCase();
        return 'U';
    };

// --- SMART LOGO ISOLATION LOGIC ---
    // 1. Check if we are running on a Tenant domain/configuration
    const isTenant = tenantConfig.id !== 'BRIGHTSPARC';

    // 2. Priority Fallback:
    // - If it's a tenant portal, try to use the tenant's specific DB logo (user?.LogoUrl) or a hardcoded tenantConfig logo.
    // - If neither exists, or if we are on the main BrightSparc hub, fall back to the global SystemSettings logo (logoUrl).
    const displayLogo = isTenant 
        ? (user?.LogoUrl || tenantConfig.logoUrl || logoUrl) 
        : logoUrl;

    return (
        <>
        <AppBar position="sticky" elevation={4} sx={{ 
            bgcolor: theme.palette.mode === 'dark' ? '#1e1e1e' : 'primary.main', 
            color: '#fff', 
            zIndex: (theme) => theme.zIndex.drawer + 1 
        }}>
            <Toolbar>
                {isMobile && <IconButton color="inherit" edge="start" onClick={handleDrawerToggle} sx={{ mr: 2 }}><MenuIcon /></IconButton>}

                <Box component={Link} to="/" sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', textDecoration: 'none', color: 'inherit' }}>
                    {displayLogo ? (
                        <Box component="img" src={displayLogo} alt={platformName} sx={{ height: 60, mr: 1.5, objectFit: 'contain', display: 'block' }}/>
                    ) : (
                        <Typography variant="h6" sx={{ fontWeight: '800', letterSpacing: '1px' }}>{platformName}</Typography>
                    )}
                </Box>

                {!isMobile && user && (
                    <Box sx={{ display: 'flex', alignItems: 'center', mr: 2, flexGrow: 1, minWidth: 0 }}>
                        
                        {/* Left Arrow */}
                        {canScrollLeft && (
                            <IconButton 
                                size="small" 
                                onClick={() => handleScroll('left')} 
                                sx={{ color: 'white', bgcolor: 'rgba(255,255,255,0.1)', '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' }, mr: 0.5, flexShrink: 0 }}
                            >
                                <KeyboardArrowLeftIcon fontSize="small" />
                            </IconButton>
                        )}

                        {/* Scrollable Container */}
                        <Box 
                            ref={scrollContainerRef}
                            onScroll={checkScroll}
                            sx={{ 
                                display: 'flex', 
                                gap: 1, 
                                overflowX: 'auto', 
                                whiteSpace: 'nowrap',
                                scrollbarWidth: 'none', 
                                msOverflowStyle: 'none', 
                                '&::-webkit-scrollbar': { display: 'none' } 
                            }}
                        >
                            {menuItems.map((item) => (
                                <Button key={item.text + item.path} color="inherit" component={Link} to={item.path} startIcon={item.icon} sx={{ flexShrink: 0, textTransform: 'none', fontWeight: location.pathname === item.path ? 'bold' : 'normal', borderBottom: location.pathname === item.path ? '2px solid white' : 'none', borderRadius: 0, px: 1.5 }}>
                                    {item.text}
                                </Button>
                            ))}
                        </Box>

                        {/* Right Arrow */}
                        {canScrollRight && (
                            <IconButton 
                                size="small" 
                                onClick={() => handleScroll('right')} 
                                sx={{ color: 'white', bgcolor: 'rgba(255,255,255,0.1)', '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' }, ml: 0.5, flexShrink: 0 }}
                            >
                                <KeyboardArrowRightIcon fontSize="small" />
                            </IconButton>
                        )}
                    </Box>
                )}

                <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                    {/* 🚨 FEATURE TOGGLE: Hide Pricing from the top desktop bar for JB Marks */}
                    {!isMobile && tenantConfig.features.showBillingDashboard && (
                        <Button component={Link} to="/pricing" color="inherit" sx={{ mr: 2, fontWeight: 'bold', '&:hover': { color: 'secondary.light' } }}>
                            Pricing
                        </Button>
                    )}

                    <Tooltip title="Toggle Theme">
                        <IconButton onClick={colorMode.toggleColorMode} color="inherit">{theme.palette.mode === 'dark' ? <Brightness7Icon /> : <Brightness4Icon />}</IconButton>
                    </Tooltip>

                    {user ? (
                        <>
                            {!isMobile && (
                                <>
                                    <Tooltip title="Messages">
                                        <IconButton color="inherit" component={Link} to="/messages" sx={{ mr: 1 }}><Badge badgeContent={unreadCount} color="error"><MailIcon /></Badge></IconButton>
                                    </Tooltip>
                                    {user.role === 'SuperUser' && (
                                        <Tooltip title="System Settings">
                                            <IconButton color="inherit" component={Link} to="/admin/settings" sx={{ mr: 1 }}><SettingsIcon /></IconButton>
                                        </Tooltip>
                                    )}
                                </>
                            )}
                            <IconButton onClick={handleMenu} sx={{ p: 0, border: '2px solid rgba(255,255,255,0.5)', ml: 1 }}>
                                <Avatar 
                                    src={user?.profilePicture || user?.ProfilePicture || ''} 
                                    sx={{ bgcolor: 'secondary.main', width: 32, height: 32, fontSize: '1rem' }}
                                >
                                    {getInitials()}
                                </Avatar>
                            </IconButton>
                            <Menu id="menu-appbar" anchorEl={anchorEl} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} keepMounted transformOrigin={{ vertical: 'top', horizontal: 'right' }} open={Boolean(anchorEl)} onClose={handleClose}>
                                <MenuItem component={Link} to="/profile" onClick={handleClose}><ListItemIcon><PersonIcon fontSize="small" /></ListItemIcon>Profile</MenuItem>
                                {user.role === 'SuperUser' && <MenuItem component={Link} to="/admin/security-hub" onClick={handleClose}><ListItemIcon><SecurityIcon fontSize="small" /></ListItemIcon>Security Hub</MenuItem>}
                                <Divider />
                                <MenuItem onClick={() => { handleClose(); setReviewOpen(true); }}><ListItemIcon><StarIcon fontSize="small" /></ListItemIcon>Rate Platform</MenuItem>
                                <Divider />
                                <MenuItem onClick={handleLogout}><ListItemIcon><LogoutIcon fontSize="small" /></ListItemIcon>Logout</MenuItem>
                            </Menu>
                        </>
                    ) : (
                        !isMobile && (
                            <Box sx={{ display: 'flex', gap: 2, ml: 1, alignItems: 'center' }}>
                                <Button component={Link} to="/login" color="inherit" variant="outlined" sx={{ borderColor: 'rgba(255,255,255,0.5)' }}>Login</Button>
                                <Button component={Link} to="/register" variant="contained" color="secondary" sx={{ fontWeight: 'bold' }}>Get Started</Button>
                            </Box>
                        )
                    )}
                </Box>
            </Toolbar>
            <Box component="nav">
                <Drawer variant="temporary" anchor="left" open={mobileOpen} onClose={handleDrawerToggle} ModalProps={{ keepMounted: true }} sx={{ display: { xs: 'block', lg: 'none' }, '& .MuiDrawer-paper': { boxSizing: 'border-box', width: 240 } }}>{drawer}</Drawer>
            </Box>
        </AppBar>
        <ReviewModal open={reviewOpen} onClose={() => setReviewOpen(false)} />
        </>
    );
};
export default Navbar;